/**
 * THE KEYS OF A HOME (ops #100), the body of the Keys card on the home's page: where each key the
 * household gave is now, one form to record a movement, and the chain under it, collapsed.
 *
 * WHERE EACH KEY IS: at the office; with a named cleaner since a day; or no longer the company's to
 * hold, drawn quieter, with what ended it read from the chain.
 *
 * ONE MOVEMENT AT A TIME. A receipt types its key's kind and name; every other movement picks one of
 * the keys the home already has, so a typo never makes a phantom key. A hand offers the active
 * cleaners the owners have marked, as every assignment picker does (`offeredForAClient`); a return
 * preselects the cleaner who holds the key. A person of the household may be named on a receipt or a
 * return to the household. When starts at now, in Florida, to the minute.
 *
 * THE SERVER HOLDS EVERY RULE (`api/src/records/keys.ts`): the key's state, the checks mark, a code in
 * the name or the note, a time to come, a time before the last movement. What it refuses is said
 * beside the form in its own sentence (`keyRefusalWords`), and what is chosen is kept. After a 201
 * the card reads the keys again, so it draws what the server holds and never what it guessed.
 */
import { useEffect, useState } from 'react';
import { KEY_KINDS, type KeyCustodyEvent, type KeyKind } from '@fk/shared';
import {
  ApiError,
  getHomeKeys,
  listCleaners,
  recordKeyMovement,
  type CleanerRow,
  type HomeKeys as HomeKeysRead,
  type NewKeyMovement,
} from '../api.ts';
import { offeredForAClient } from '../checks.ts';
import { isDay, isTimeOfDay } from '../format.ts';
import {
  KEY_EVENTS,
  KEY_EVENT_LABELS,
  KEY_KIND_WORDS,
  NAMES_A_CLEANER,
  NAMES_A_PERSON,
  floridaNow,
  keyChoiceWords,
  keyName,
  keyNowWords,
  keyRefusalWords,
  keysFor,
  movementWords,
  noKeyWords,
  type PersonOnFile,
} from '../keys.ts';
import { floridaInstant } from '../screens/visit-detail/CallOffCard.tsx';
import { Button, SelectField, TextAreaField, TextField } from './Field.tsx';
import { Busy, ErrorNotice, SavedNotice } from './Notice.tsx';

export function HomeKeys({
  siteId,
  people,
}: {
  siteId: string;
  /** The household's people, to name one on a receipt or a return, and in the chain. */
  people: readonly PersonOnFile[];
}) {
  const [read, setRead] = useState<HomeKeysRead | null>(null);
  const [failed, setFailed] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(null);
    getHomeKeys(siteId)
      .then((answer) => {
        if (live) setRead(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The keys did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [siteId, attempt]);

  if (failed) {
    return (
      <div data-home-keys>
        <ErrorNotice
          title="The keys did not load"
          message={failed.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }
  if (!read) {
    return (
      <div data-home-keys>
        <Busy what="the keys" />
      </div>
    );
  }

  const { keys, chain } = read;

  return (
    <div data-home-keys className="flex flex-col gap-3">
      {keys.length === 0 ? (
        <p className="text-sm text-navy/70">
          No key of this home is on record. Record one when the household gives it.
        </p>
      ) : (
        <ul className="flex flex-col">
          {keys.map((key) => (
            <li
              key={`${key.keyKind}\n${key.keyLabel}`}
              data-key-at={key.at}
              className={`flex flex-col border-b border-navy/8 py-1.5 text-sm last:border-b-0 ${key.at === 'gone' ? 'text-navy/55' : ''}`}
            >
              <span className={key.at === 'gone' ? '' : 'font-medium'}>{keyName(key)}</span>
              <span className="text-xs leading-4">{keyNowWords(key, chain)}</span>
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <KeyMoveForm
          siteId={siteId}
          keys={keys}
          people={people}
          onCancel={() => setOpen(false)}
          onRecorded={() => {
            setOpen(false);
            setSaved(true);
            setAttempt((n) => n + 1);
          }}
        />
      ) : (
        <div>
          <Button
            tone="quiet"
            onClick={() => {
              setSaved(false);
              setOpen(true);
            }}
          >
            Record a key movement
          </Button>
        </div>
      )}
      {saved ? <SavedNotice>Recorded.</SavedNotice> : null}

      {chain.length > 0 ? (
        <details data-key-chain className="group">
          <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium desk:min-h-9">
            {`The chain, ${chain.length} ${chain.length === 1 ? 'movement' : 'movements'}`}
          </summary>
          <ol className="mt-1 flex flex-col">
            {chain.map((move) => {
              const words = movementWords(move, people);
              return (
                <li key={move.id} className="border-b border-navy/8 py-1.5 last:border-b-0">
                  <p className="text-sm">{words.what}</p>
                  <p className="text-xs leading-4 text-navy/65">{words.when}</p>
                  {move.note ? (
                    <p className="text-xs leading-4 whitespace-pre-wrap">{move.note}</p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </details>
      ) : null}
    </div>
  );
}

/**
 * The one form. Its choices follow the event: a receipt types the key, every other movement picks it;
 * a hand and a return name the cleaner; a receipt and a return to the household may name a person.
 */
function KeyMoveForm({
  siteId,
  keys,
  people,
  onCancel,
  onRecorded,
}: {
  siteId: string;
  keys: HomeKeysRead['keys'];
  people: readonly PersonOnFile[];
  onCancel: () => void;
  onRecorded: () => void;
}) {
  const [start] = useState(() => floridaNow(new Date()));
  const [event, setEvent] = useState<KeyCustodyEvent | ''>('');
  const [kind, setKind] = useState<KeyKind | ''>('');
  const [label, setLabel] = useState('');
  const [which, setWhich] = useState('');
  const [cleanerId, setCleanerId] = useState('');
  const [contactId, setContactId] = useState('');
  const [day, setDay] = useState(start.day);
  const [time, setTime] = useState(start.time);
  const [note, setNote] = useState('');
  const [crew, setCrew] = useState<readonly CleanerRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listCleaners()
      .then((answer) => {
        if (live) setCrew(answer.cleaners);
      })
      .catch(() => {
        // The picker offers nobody but a return's holder; the rest of the form stands.
      });
    return () => {
      live = false;
    };
  }, []);

  const typed = event === 'received_from_client';
  const choices = event === '' ? [] : keysFor(event, keys);
  const chosen = which === '' ? undefined : choices[Number(which)];
  const namesCleaner = event !== '' && NAMES_A_CLEANER.includes(event);
  const namesPerson = event !== '' && NAMES_A_PERSON.includes(event);

  const holder = event === 'returned_by_cleaner' ? (chosen?.cleaner ?? null) : null;
  const offered: { id: string; displayName: string }[] =
    event === 'handed_to_cleaner'
      ? crew.filter((cleaner) => offeredForAClient(cleaner))
      : [...(holder ? [holder] : []), ...crew.filter((cleaner) => cleaner.id !== holder?.id)];
  const nameOf = new Map(offered.map((cleaner) => [cleaner.id, cleaner.displayName]));

  const key = typed
    ? kind !== '' && label.trim() !== ''
      ? { keyKind: kind, keyLabel: label.trim() }
      : null
    : chosen
      ? { keyKind: chosen.keyKind, keyLabel: chosen.keyLabel }
      : null;
  const ready =
    event !== '' &&
    key !== null &&
    (!namesCleaner || cleanerId !== '') &&
    isDay(day) &&
    isTimeOfDay(time);

  const chooseEvent = (next: KeyCustodyEvent) => {
    setEvent(next);
    setWhich('');
    setCleanerId('');
    setContactId('');
    setRefused(null);
  };

  const chooseKey = (index: string) => {
    setWhich(index);
    setRefused(null);
    const picked = choices[Number(index)];
    if (event === 'returned_by_cleaner') setCleanerId(picked?.cleaner?.id ?? '');
  };

  const submit = async () => {
    if (!ready) return;
    const body: NewKeyMovement = {
      ...key,
      event,
      ...(namesCleaner ? { cleanerId } : {}),
      ...(namesPerson && contactId !== '' ? { contactId } : {}),
      occurredAt: floridaInstant(day, time),
      ...(note.trim() !== '' ? { note: note.trim() } : {}),
    };
    setBusy(true);
    setRefused(null);
    try {
      await recordKeyMovement(siteId, body);
      onRecorded();
    } catch (thrown) {
      setRefused(thrown instanceof ApiError ? keyRefusalWords(thrown) : 'It was not recorded.');
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col">
      <SelectField
        id="key-event"
        label="What happened"
        value={event}
        onChange={chooseEvent}
        options={KEY_EVENTS}
        toLabel={(value) => KEY_EVENT_LABELS[value]}
        placeholder="Choose what happened"
        required
      />

      {typed ? (
        <>
          <SelectField
            id="key-kind"
            label="Kind of key"
            value={kind}
            onChange={setKind}
            options={KEY_KINDS}
            toLabel={(value) => KEY_KIND_WORDS[value]}
            placeholder="Choose the kind"
            required
          />
          <TextField
            id="key-label"
            label="The key's name"
            value={label}
            onChange={setLabel}
            hint="How the office tells it apart, like brass key, blue tag. Never a code: codes are kept with the home's codes."
            required
          />
        </>
      ) : event !== '' ? (
        choices.length === 0 ? (
          <p className="mb-4 text-sm text-navy/70">{noKeyWords(event)}</p>
        ) : (
          <SelectField
            id="key-which"
            label="Which key"
            value={which}
            onChange={chooseKey}
            options={choices.map((_, index) => String(index))}
            toLabel={(index) => {
              const one = choices[Number(index)];
              return one ? keyChoiceWords(one) : index;
            }}
            placeholder="Choose the key"
            required
          />
        )
      ) : null}

      {namesCleaner ? (
        <SelectField
          id="key-cleaner"
          label={event === 'handed_to_cleaner' ? 'Handed to' : 'Returned by'}
          value={cleanerId}
          onChange={setCleanerId}
          options={offered.map((cleaner) => cleaner.id)}
          toLabel={(id) => nameOf.get(id) ?? id}
          placeholder="Choose the cleaner"
          hint={
            event === 'handed_to_cleaner'
              ? 'Active cleaners whose checks an owner has marked.'
              : 'The cleaner who holds the key.'
          }
          required
        />
      ) : null}

      {namesPerson ? (
        <SelectField
          id="key-contact"
          label={event === 'received_from_client' ? 'Given by' : 'Given back to'}
          value={contactId}
          onChange={setContactId}
          options={people.map((person) => person.id)}
          toLabel={(id) => people.find((person) => person.id === id)?.displayName ?? id}
          placeholder="Nobody named"
        />
      ) : null}

      <div className="grid gap-x-3 @md:grid-cols-2">
        <TextField
          id="key-day"
          label="Day it happened"
          type="date"
          value={day}
          onChange={setDay}
          max={start.day}
          required
        />
        <TextField
          id="key-time"
          label="Time, Florida"
          type="time"
          value={time}
          onChange={setTime}
          required
        />
      </div>

      <TextAreaField
        id="key-note"
        label="Note"
        value={note}
        onChange={setNote}
        hint="How it was given, if it matters. At most 500 characters, and never a code."
      />

      {refused ? <ErrorNotice title="Not recorded" message={refused} /> : null}

      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={() => void submit()} disabled={busy || !ready}>
          {busy ? 'Recording' : 'Record it'}
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
